import { DataGrid, DataGridFilterState, Spinner } from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { C2_EDDY_LOAD_STATUSES, type C2EddyLoadStatus, useC2EddyLoads } from '@/entities/integrations';
import { dataGridHeightByRows } from '@/shared/lib';
import { fromDataGridFilterValue } from '@/shared/lib/utils/tableFilterValue';

import { useC2EddyLoadColumns } from './useC2EddyLoadColumns';

const PAGE_SIZE = 20;

/** Потолок журнала в карточке: страница целиком выше — остаток прокручивается внутри самого грида */
const JOURNAL_MAX_HEIGHT = 480;

/** Отбор по результату: значение отбора приходит от грида, а в запрос идут только известные результаты */
const toStatusFilter = (filters: DataGridFilterState): C2EddyLoadStatus | undefined => {
  const value = fromDataGridFilterValue(filters.status);
  return C2_EDDY_LOAD_STATUSES.find((status) => status === value);
};

/**
 * Журнал загрузок обращений HelpDeskEddy: по строке на обращение, свежие сверху, с результатом
 * загрузки и её описанием. Листается и отбирается по результату на сервере.
 */
export const C2EddyLoadsJournal: FC = () => {
  const { t } = useTranslation(['integrations']);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<DataGridFilterState>({});
  const columns = useC2EddyLoadColumns();

  const status = toStatusFilter(filters);
  const { data, isLoading } = useC2EddyLoads({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    status,
  });

  const items = data?.items ?? [];

  // Отбор применяется ко всему журналу, а не к открытой странице: прежнее смещение на новом
  // отборе показало бы пустую страницу при ненулевом счётчике
  const handleFiltersChange = (nextFilters: DataGridFilterState) => {
    setFilters(nextFilters);
    setPage(0);
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium">{t('integrations:c2Eddy.journal.title')}</p>
      <DataGrid
        className="w-full"
        columns={columns}
        dataSource={{
          mode: 'paginated',
          totalCount: data?.total ?? 0,
          page,
          pageSize: PAGE_SIZE,
          onPageChange: setPage,
          data: items,
        }}
        getRowId={(row) => String(row.eddyTicketId)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        height={dataGridHeightByRows(items.length, JOURNAL_MAX_HEIGHT)}
        emptyState={
          isLoading ? (
            <Spinner className="size-6" />
          ) : (
            <span className="text-muted-foreground">
              {/* Пустая выдача под отбором — не пустой журнал: иначе загрузки выглядят отсутствующими */}
              {t(status ? 'integrations:c2Eddy.journal.emptyByFilter' : 'integrations:c2Eddy.journal.empty')}
            </span>
          )
        }
        ariaLabel={t('integrations:c2Eddy.journal.title')}
      />
    </div>
  );
};
